import './PlanHistoryField.style.less'

import { Avatar, Card, List, Popover, Row } from 'antd'
import Meta from 'antd/lib/card/Meta'
import { capitalize, find, get as _get, orderBy, slice } from 'lodash'
import moment from 'moment'
import { HistoryOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons'
import { STATUS_LABEL_COLOR } from '../StatusField/StatusField.constant'
import { ITaskActivity } from '@/screens/Order/Pages/OrderOverview/TaskTemplate.interface'
import { IActivity } from '@/screens/Order/interface'
import { Capitalize, OrdinalSuffix, isEmptyValue } from '@/utils/common'
import { ACTIVITY_LOG_TYPE } from '@/screens/Order/constant/activity_type'
import cx from 'classnames'

function IsSameDate(start, end) {
  return moment(start).format('DD/MM/YYYY') == moment(end).format('DD/MM/YYYY')
}

function FormatDate(date) {
  const parsedDate = moment(date)
  return parsedDate.isValid() ? parsedDate.format('DD MMM YYYY') : ''
}

function RenderPlanDate({ data }: any) {
  const { json_value, created_by } = data
  const {
    previous_plan_end_date,
    previous_plan_start_date,
    plan_start_date,
    plan_end_date,
    is_range_date,
    is_range_date_prev,
  } = json_value
  const { first_name, last_name } = created_by
  const prePlanEndDate = moment(previous_plan_start_date).isValid()
  const displaySet = prePlanEndDate ? 'update ' : 'set '
  let displayDate = <></>

  const renderDate = (startDate, endDate, isRankDate) => {
    // SPECIAL CASE: The values for is_range_date and is_range_date_prev exist when the tasks are imported from the script.
    if (isRankDate === false) {
      return <span className="name-bold">{FormatDate(startDate)}</span>
    }

    return (
      <span className="name-bold">
        {FormatDate(startDate)}
        {!IsSameDate(startDate, endDate) ? ' - ' + FormatDate(endDate) : ''}
      </span>
    )
  }
  if (prePlanEndDate) {
    displayDate = (
      <span>
        {' from '}
        {renderDate(
          previous_plan_start_date,
          previous_plan_end_date,
          is_range_date_prev
        )}
        {' to '}
        {renderDate(plan_end_date, plan_start_date, is_range_date)}
      </span>
    )
  } else {
    displayDate = (
      <span>
        {'to'} {renderDate(plan_end_date, plan_start_date, is_range_date)}
      </span>
    )
  }

  return (
    <p style={{ marginBottom: 0 }}>
      <span className="name-bold">
        {' '}
        {first_name} {last_name}
      </span>{' '}
      {displaySet}
      <span className="name-bold">Planned Date</span> {displayDate}
    </p>
  )
}

function RenderAssignee({ data }: any) {
  const { json_value, created_by } = data
  const { activity_type, previous_assignee, assignee } = json_value
  const { first_name, last_name } = created_by
  return (
    <p>
      <span className="name-bold">
        {' '}
        {first_name} {last_name}
      </span>{' '}
      {activity_type == ACTIVITY_LOG_TYPE.SET_TASK_ASSIGNEE ? 'set' : 'updated'}{' '}
      <span className="name-bold">Assignee</span>{' '}
      {activity_type == ACTIVITY_LOG_TYPE.SET_TASK_ASSIGNEE ? ' to' : 'from '}
      <span className="name-bold">
        {!isEmptyValue(previous_assignee) &&
          previous_assignee?.first_name +
            ' ' +
            previous_assignee?.last_name}{' '}
      </span>
      {!isEmptyValue(previous_assignee) && 'to '}
      <span className="name-bold">
        {assignee?.first_name + ' ' + assignee?.last_name}
      </span>
    </p>
  )
}

function RenderStatus({ data }: any) {
  const { json_value, created_by } = data
  const { status, previous_status, submit_round, actual_date, activity_type } =
    json_value
  const { first_name, last_name } = created_by
  return (
    <p style={{ marginBottom: 0 }}>
      <span className="name-bold">
        {' '}
        {first_name} {last_name}
      </span>{' '}
      {activity_type == ACTIVITY_LOG_TYPE.SET_TASK_STATUS ? 'set' : 'updated'}
      <span
        className={cx(
          'name-bold',
          previous_status == 'approved' ? 'text-success' : '',
          previous_status == 'rejected' ? 'text-danger' : ''
        )}
      >
        {' '}
        {Capitalize(previous_status)}{' '}
      </span>{' '}
      to{' '}
      <span
        className={cx(
          'name-bold',
          status == 'approved' ? 'text-success' : '',
          status == 'rejected' ? 'text-danger' : ''
        )}
      >
        {' '}
        {Capitalize(status)}
      </span>{' '}
      {status == 'submitted'
        ? submit_round > 0
          ? `(${OrdinalSuffix(submit_round + 1)} time)`
          : ''
        : ''}{' '}
      on <span className="name-bold">{FormatDate(actual_date)}</span>
    </p>
  )
}

interface Prop {
  activity: ITaskActivity[]
}

export function PlanHistoryField(prop: Prop) {
  const { activity } = prop

  const _showActivityTitle = (history: IActivity): any => {
    if (history?.value_status)
      return capitalize(history.value_status).replace('_', ' ')
    return !isNaN(history?.json_value?.current_status as any)
      ? `${history?.json_value?.current_status}%`
      : capitalize(history?.json_value?.current_status).replace('_', ' ')
  }

  return (
    <div className="plan-field-history-container">
      {activity?.length > 0 && (
        <div className="ant-space ant-space-horizontal ant-space-align-center">
          <Popover
            placement="bottom"
            className=""
            content={
              <List
                className="approval-history-list"
                itemLayout="horizontal"
                dataSource={slice(
                  orderBy(activity, ['created_date'], ['desc'])
                )}
                renderItem={(history: any) =>
                  !isEmptyValue(history?.json_value.activity_type) ? (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <Popover
                            placement="bottom"
                            content={
                              <Card className="p-2" bordered={false}>
                                <Meta
                                  avatar={
                                    <Avatar
                                      src={`${history?.created_by?.avatar?.url_path}`}
                                      alt={`${
                                        history.created_by.first_name
                                      }${' '}
                                ${history.created_by.last_name}`}
                                      size={32}
                                    >
                                      {history.created_by.first_name.charAt(0)}
                                      {history.created_by.last_name.charAt(0)}
                                    </Avatar>
                                  }
                                  title={`${history.created_by.first_name}${' '}
                            ${history.created_by.last_name}`}
                                />
                                <p>
                                  {' '}
                                  {(history?.created_by?.property_value?.value
                                    ?.length &&
                                    find(
                                      history?.created_by?.property_value
                                        ?.value,
                                      ['key', 'position']
                                    )?.value) ??
                                    `user_role`}
                                </p>
                                <div>
                                  <h6>Contact</h6>
                                  <p>
                                    <PhoneOutlined /> &nbsp;
                                    <a
                                      href={`tel:${
                                        history?.created_by?.property_value
                                          ?.value?.length &&
                                        find(
                                          history?.created_by?.property_value
                                            ?.value,
                                          ['key', 'phoneNumber']
                                        )?.value
                                      }`}
                                    >
                                      {history?.created_by?.property_value
                                        ?.value?.length &&
                                        find(
                                          history?.created_by?.property_value
                                            ?.value,
                                          ['key', 'phoneNumber']
                                        )?.value}
                                    </a>
                                  </p>

                                  <p>
                                    <MailOutlined /> &nbsp;
                                    <a
                                      href={`mailto:${
                                        history?.created_by?.email ?? ''
                                      }`}
                                    >
                                      {history?.created_by?.email ?? ''}
                                    </a>
                                  </p>
                                </div>
                              </Card>
                            }
                          >
                            <Avatar
                              src={`${history?.created_by?.avatar?.url_path}`}
                              size={32}
                            >
                              {history.created_by.first_name.charAt(0)}
                              {history.created_by.last_name.charAt(0)}
                            </Avatar>
                          </Popover>
                        }
                        title={
                          history.json_value.activity_type ==
                            ACTIVITY_LOG_TYPE.SET_TASK_PLAN_DATE ||
                          history.json_value.activity_type ==
                            ACTIVITY_LOG_TYPE.UPDATE_TASK_PLAN_DATE ? (
                            <RenderPlanDate data={history} />
                          ) : history.json_value.activity_type ==
                              ACTIVITY_LOG_TYPE.SET_TASK_ASSIGNEE ||
                            history.json_value.activity_type ==
                              ACTIVITY_LOG_TYPE.UPDATE_TASK_ASSIGNEE ? (
                            <RenderAssignee data={history} />
                          ) : (
                            <RenderStatus data={history} />
                          )
                        }
                        description={
                          <span className="history-status-date-text">
                            {moment(history.created_date).format('DD MMM gggg')}
                          </span>
                        }
                      />
                    </List.Item>
                  ) : (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <Popover
                            placement="bottom"
                            content={
                              <Card className="p-2" bordered={false}>
                                <Meta
                                  avatar={
                                    <Avatar
                                      src={`${history?.created_by?.avatar?.url_path}`}
                                      alt={`${
                                        history.created_by.first_name
                                      }${' '}
                                    ${history.created_by.last_name}`}
                                      size={32}
                                    >
                                      {history.created_by.first_name.charAt(0)}
                                      {history.created_by.last_name.charAt(0)}
                                    </Avatar>
                                  }
                                  title={`${history.created_by.first_name}${' '}
                                ${history.created_by.last_name}`}
                                />
                                <p>
                                  {' '}
                                  {(history?.created_by?.property_value?.value
                                    ?.length &&
                                    find(
                                      history?.created_by?.property_value
                                        ?.value,
                                      ['key', 'position']
                                    )?.value) ??
                                    `user_role`}
                                </p>
                                <div>
                                  <h6>Contact</h6>
                                  <p>
                                    <PhoneOutlined /> &nbsp;
                                    <a
                                      href={`tel:${
                                        history?.created_by?.property_value
                                          ?.value?.length &&
                                        find(
                                          history?.created_by?.property_value
                                            ?.value,
                                          ['key', 'phoneNumber']
                                        )?.value
                                      }`}
                                    >
                                      {history?.created_by?.property_value
                                        ?.value?.length &&
                                        find(
                                          history?.created_by?.property_value
                                            ?.value,
                                          ['key', 'phoneNumber']
                                        )?.value}
                                    </a>
                                  </p>

                                  <p>
                                    <MailOutlined /> &nbsp;
                                    <a
                                      href={`mailto:${
                                        history?.created_by?.email ?? ''
                                      }`}
                                    >
                                      {history?.created_by?.email ?? ''}
                                    </a>
                                  </p>
                                </div>
                              </Card>
                            }
                          >
                            <Avatar
                              src={`${history?.created_by?.avatar?.url_path}`}
                              size={32}
                            >
                              {history.created_by.first_name.charAt(0)}
                              {history.created_by.last_name.charAt(0)}
                            </Avatar>
                          </Popover>
                        }
                        title={
                          <div>
                            <span
                              className={`status-text ${_get(
                                STATUS_LABEL_COLOR,
                                _get(
                                  history,
                                  'value_status',
                                  _get(history, 'json_value.current_status')
                                )
                              )} ${history?.json_value?.current_status}`}
                            >
                              {_showActivityTitle(history)}
                            </span>
                            <span className="by-text">
                              {' '}
                              by {history?.created_by?.first_name}{' '}
                              {history?.created_by?.last_name}
                            </span>
                          </div>
                        }
                        description={
                          <span className="history-status-date-text">
                            {moment(history.created_date).format('DD MMM gggg')}
                          </span>
                        }
                      />
                    </List.Item>
                  )
                }
              />
            }
          >
            <Row align={'middle'}>
              <HistoryOutlined />
              <p className="label">History</p>
            </Row>
          </Popover>
        </div>
      )}
    </div>
  )
}
